/**
 * CatalogContext — catálogo de ejercicios cargado lazy al primer uso.
 *
 * Decisión: NO precargar al login (los 1408 ejercicios son ~1MB; la mayoría
 * de pantallas no los necesitan). Cada consumer llama `await ensureLoaded()`
 * o usa `loaded`/`loading` para decidir su propio fallback.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { listExercises, type Exercise } from "../api/exercises";
import { fuzzyMatch, normalize } from "../lib/normalizeText";

interface CatalogValue {
  exercises: Exercise[];
  loaded: boolean;
  loading: boolean;
  error: string | null;
  /** Asegura que el catálogo está cargado. Idempotente — si ya está, resuelve inmediato. */
  ensureLoaded: () => Promise<void>;
  /** Filtro client-side sobre la lista ya cargada. Si no está cargada, devuelve [].
   *  `muscle` puede ser un string único o un array (para grupos como "Espalda" que
   *  agrega varios músculos del backend). */
  search: (query?: string, muscle?: string | string[]) => Exercise[];
  /** Re-fetch fresco. Útil tras cambios admin. */
  reload: () => Promise<void>;
}

const CatalogContext = createContext<CatalogValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Singleton de promise para que múltiples consumers paralelos no disparen N fetches.
  const inflight = useRef<Promise<void> | null>(null);

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listExercises();
      setExercises(list);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const ensureLoaded = useCallback(async () => {
    if (loaded) return;
    if (inflight.current) return inflight.current;
    inflight.current = fetchOnce().finally(() => {
      inflight.current = null;
    });
    return inflight.current;
  }, [loaded, fetchOnce]);

  const reload = useCallback(async () => {
    inflight.current = fetchOnce().finally(() => {
      inflight.current = null;
    });
    return inflight.current;
  }, [fetchOnce]);

  const search = useCallback(
    (query?: string, muscle?: string | string[]): Exercise[] => {
      if (!loaded) return [];
      // muscle puede ser un string único o un array (para "grupos" como
      // "Espalda" = ["Espalda", "Espalda alta", "Dorsal", "Lumbares", ...])
      const muscles = Array.isArray(muscle)
        ? muscle.map(normalize).filter(Boolean)
        : muscle
        ? [normalize(muscle)]
        : [];
      const q = (query || "").trim();
      return exercises.filter((ex) => {
        if (muscles.length > 0) {
          const exMuscle = normalize(ex.muscle || ex.primary_muscle || ex.category);
          if (!muscles.includes(exMuscle)) return false;
        }
        if (q) {
          // `fuzzyMatch` tokeniza + stem español (plural→singular) y exige que
          // cada token de la query aparezca como substring en algún token del
          // nombre/equipment/músculo. Reemplaza el viejo `hay.includes(q)` que
          // requería substring EXACTO de la query completa — fallaba con
          // plural vs singular ("elevaciones laterales" vs "Elevación Lateral
          // con Mancuerna") y con orden de palabras invertido. Matchea ambos
          // idiomas (español + name_en) así "fly", "good morning" siguen
          // funcionando.
          const hay = `${ex.name} ${ex.name_en || ""} ${ex.muscle || ""} ${ex.equipment || ""}`;
          if (!fuzzyMatch(q, hay)) return false;
        }
        return true;
      });
    },
    [exercises, loaded],
  );

  const value = useMemo<CatalogValue>(
    () => ({ exercises, loaded, loading, error, ensureLoaded, search, reload }),
    [exercises, loaded, loading, error, ensureLoaded, search, reload],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

/** Stub no-op para cuando no hay <CatalogProvider> (vista pública /p/{code}).
 * RoutineSection en readonly llama el hook pero no usa el catálogo —
 * los gifUrls ya vienen guardados en el plan. Sin este stub la vista
 * pública crashearía con "useCatalog() debe estar dentro de <CatalogProvider>". */
const CATALOG_STUB: CatalogValue = {
  exercises: [],
  loaded: false,
  loading: false,
  error: null,
  ensureLoaded: async () => {},
  search: () => [],
  reload: async () => {},
};

export function useCatalog(): CatalogValue {
  return useContext(CatalogContext) ?? CATALOG_STUB;
}
